import * as React from "react";
import type { MapPortalData } from "~mangomap-ui/utils/mock-map-portal-data";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Copy as CopyIcon } from "@phosphor-icons/react/Copy";
import { DotsThree as DotsThreeIcon } from "@phosphor-icons/react/DotsThree";
import { Trash as TrashIcon } from "@phosphor-icons/react/Trash";

export interface MapCardMenuProps {
	map: MapPortalData;
	onDuplicate?: (event: { map: MapPortalData }) => void;
	onDelete?: (event: { map: MapPortalData }) => void;
	size?: "small" | "medium";
	canDelete?: boolean;
}

export function MapCardMenu({
	map,
	onDuplicate,
	onDelete,
	size = "small",
	canDelete = false,
}: MapCardMenuProps): React.JSX.Element {
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
		null,
	);

	// Simplified permission logic
	const canEdit = canDelete; // For now, edit and delete permissions are the same

	// If user cannot edit or delete, don't render the menu at all
	if (!canEdit && !canDelete) {
		return <></>;
	}

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation(); // Prevent card click when clicking menu button
		event.preventDefault(); // Prevent link navigation when parent is a Next.js Link
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleDuplicate = (e) => {
		e.stopPropagation();
		e.preventDefault();
		handleClose();
		onDuplicate?.({ map });
	};

	const handleDelete = (e) => {
		e.stopPropagation();
		e.preventDefault();
		handleClose();
		onDelete?.({ map });
	};

	const open = Boolean(anchorEl);
	const iconSize = size === "small" ? 16 : 20;
	const buttonSize = size === "small" ? "small" : "medium";

	return (
		<>
			<IconButton
				onClick={handleClick}
				size={buttonSize}
				aria-label="Map actions menu"
				sx={{
					bgcolor: "var(--mui-palette-background-paper)",
					color: "var(--mui-palette-text-primary)",
					opacity: 0.9,
					"&:hover": {
						bgcolor: "var(--mui-palette-action-hover)",
						opacity: 1,
					},
					backdropFilter: "blur(4px)",
					border: "1px solid var(--mui-palette-divider)",
					boxShadow: "var(--mui-shadows-1)",
				}}>
				<DotsThreeIcon
					size={iconSize}
					weight="bold"
				/>
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				BackdropProps={{
					invisible: true,
					onClick: (e) => {
						e.stopPropagation();
						e.preventDefault();
						handleClose();
					},
				}}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							boxShadow: "var(--mui-shadows-16)",
							border: "1px solid var(--mui-palette-divider)",
							minWidth: 160,
						},
					},
				}}>
				{canEdit && (
					<MenuItem onClick={handleDuplicate}>
						<ListItemIcon>
							<CopyIcon size={20} />
						</ListItemIcon>
						Duplicate Map
					</MenuItem>
				)}

				{canDelete && (
					<MenuItem
						onClick={handleDelete}
						sx={{
							color: "var(--mui-palette-error-main)",
							"& .MuiListItemIcon-root": {
								color: "var(--mui-palette-error-main)",
							},
						}}>
						<ListItemIcon>
							<TrashIcon size={20} />
						</ListItemIcon>
						Delete Map
					</MenuItem>
				)}
			</Menu>
		</>
	);
}
